import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Bell, CheckCheck, Clock, AlarmClock } from 'lucide-react';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';

const when = (iso) => { try { return format(parseISO(iso), 'd MMM, HH:mm'); } catch { return ''; } };

/** In-app notifications (hours change outcomes). `compact` renders just the unread banner for Home. */
export const PortalNotifications = ({ compact = false }) => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState({ items: [], unread: 0 });

  const load = useCallback(() => api.get('/portal/notifications').then(r => setData(r.data)).catch(() => {}), [api]);
  useEffect(() => { load(); }, [load]);

  const markAll = async () => { try { await api.post('/portal/notifications/read', {}); load(); } catch { /* ignore */ } };
  const open = async (n) => {
    if (!n.read) { try { await api.post('/portal/notifications/read', { ids: [n.id] }); } catch { /* ignore */ } }
    if (n.link) navigate(n.link); else load();
  };
  const snooze = async (e, n) => {
    e.stopPropagation();
    try {
      await api.post(`/portal/notifications/${n.id}/snooze`);
      toast.success("OK — we'll remind you once more at 8pm");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Could not snooze'); }
  };

  if (compact) {
    const unread = data.items.filter(n => !n.read);
    if (!unread.length) return null;
    return (
      <div className="space-y-2" data-testid="home-notifications">
        {unread.slice(0, 3).map(n => (
          <div key={n.id} className="rounded-xl border border-[#F5A623]/40 bg-[#F5A623]/10 p-3" data-testid={`home-notification-${n.id}`}>
            <button type="button" onClick={() => open(n)} className="w-full text-left flex gap-3 active:scale-[0.99]" data-testid={`home-notification-open-${n.id}`}>
              <Bell className="w-5 h-5 text-[#F5A623] shrink-0 mt-0.5" />
              <span className="min-w-0"><span className="block text-white text-sm font-semibold">{n.title}</span><span className="block text-gray-300 text-xs mt-0.5">{n.body}</span></span>
            </button>
            {n.can_snooze && (
              <div className="flex gap-2 mt-2.5 pl-8">
                <button type="button" onClick={() => open(n)} className="h-9 px-3 rounded-lg bg-[#F5A623] text-[#1E2530] text-xs font-bold active:scale-95" data-testid={`home-notification-add-${n.id}`}>Add hours</button>
                <button type="button" onClick={(e) => snooze(e, n)} className="h-9 px-3 rounded-lg bg-white/10 text-gray-200 text-xs font-semibold flex items-center gap-1 active:scale-95" data-testid={`home-notification-snooze-${n.id}`}><AlarmClock className="w-3.5 h-3.5" /> I'll do it later</button>
              </div>
            )}
          </div>
        ))}
        {unread.length > 3 && <button type="button" onClick={() => navigate('/portal/profile')} className="text-xs text-[#F5A623] px-1">+{unread.length - 3} more in Profile</button>}
      </div>
    );
  }

  return (
    <Card className="bg-[#2A3240] border-white/10" data-testid="profile-notifications">
      <CardHeader className="pb-3 flex-row items-center justify-between space-y-0">
        <CardTitle className="text-white text-lg flex items-center gap-2"><Bell className="w-5 h-5 text-[#F5A623]" /> Notifications{data.unread > 0 && <span className="text-xs bg-[#F5A623] text-[#1E2530] rounded-full px-2 py-0.5 font-bold" data-testid="profile-notifications-unread">{data.unread}</span>}</CardTitle>
        {data.unread > 0 && <button type="button" onClick={markAll} className="text-xs text-gray-400 hover:text-white flex items-center gap-1" data-testid="profile-notifications-read-all"><CheckCheck className="w-3.5 h-3.5" /> Mark all read</button>}
      </CardHeader>
      <CardContent>
        {data.items.length === 0 ? (
          <p className="text-gray-500 text-sm text-center py-4" data-testid="profile-notifications-empty">Nothing yet — updates from the office will show here.</p>
        ) : (
          <div className="space-y-2">
            {data.items.map(n => (
              <button key={n.id} type="button" onClick={() => open(n)} className={`w-full text-left rounded-xl p-3 flex gap-3 active:scale-[0.99] ${n.read ? 'bg-[#1E2530]' : 'bg-[#F5A623]/10 border border-[#F5A623]/30'}`} data-testid={`profile-notification-${n.id}`}>
                <Clock className={`w-4 h-4 shrink-0 mt-0.5 ${n.read ? 'text-gray-500' : 'text-[#F5A623]'}`} />
                <span className="min-w-0 flex-1">
                  <span className="flex items-center gap-2"><span className={`text-sm font-semibold ${n.read ? 'text-gray-300' : 'text-white'}`}>{n.title}</span><span className="ml-auto text-[10px] text-gray-500 shrink-0">{when(n.created_at)}</span></span>
                  <span className="block text-gray-400 text-xs mt-0.5">{n.body}</span>
                  {n.snoozed && <span className="inline-flex items-center gap-1 mt-1 text-[10px] text-amber-300" data-testid={`profile-notification-snoozed-${n.id}`}><AlarmClock className="w-3 h-3" /> Snoozed — one more reminder at 8pm</span>}
                </span>
              </button>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
